import clsx from 'clsx'

import { CustomFieldType } from '@tribeplatform/gql-client/types'
import { FieldView } from '@tribeplatform/react-components/CustomField'
import { useBlock } from '@tribeplatform/react-slate-kit/hooks'

import { ActionBar } from '../fields/ActionBar'
import { CardBanner } from '../fields/CardBanner'
import { CardPostContent } from '../fields/CardPostContent'
import { GalleryBanner } from '../fields/GalleryBanner'
import { GalleryMemberInfo } from '../fields/GalleryMemberInfo'
import { GalleryPostContent } from '../fields/GalleryPostContent'
import { InfoBarField } from '../fields/InfoBarField'
import { ListAvatar } from '../fields/ListAvatar'
import { ListPostSummary } from '../fields/ListPostSummary'
import { PinnedReply } from '../fields/PinnedReply'
import { PostInfoBarField } from '../fields/PostInfoBarField'
import { PostTitle } from '../fields/PostTitle'
import { PublishDate } from '../fields/PublishDate'
import { ReactButton } from '../fields/ReactButton'
import { ReactionCount } from '../fields/ReactionCount'
import { ReplyCount } from '../fields/ReplyCount'
import { ReplyListField } from '../fields/ReplyListField'
import { Tags } from '../fields/Tags'
import { FieldProps, PostsBlockProps } from '../types'
import { getMediaUrl } from '../utils'

export const Field = ({
  field,
  post,
  postTypes,
  viewStyle,
  activeFieldId,
  linkTo,
  preview,
  isSinglePost,
}: FieldProps) => {
  const {
    extraContext: { activated },
  } = useBlock<PostsBlockProps, { activated: boolean }>()
  const mediaUrl = getMediaUrl({ field, post })

  const stretchX = clsx(
    viewStyle === 'list' && field.settings?.stretchX && 'flex-1',
    viewStyle === 'gallery' && field.settings?.fullWidth && 'basis-full',
    viewStyle === 'carousel' && field.settings?.fullWidth && 'basis-full',
    viewStyle === 'card' && field.settings?.fullWidth && 'basis-full',
  )
  const titleSizeClass = clsx(
    field.settings?.titleSize === 'sm' && 'text-lg font-semibold',
    (field.settings?.titleSize === 'md' || !field.settings?.titleSize) &&
      'text-xl',
    field.settings?.titleSize === 'lg' && 'text-2xl',
  )
  const highlighted = activated && activeFieldId === field.id
  // If a field should stretch, then we should allow it to be empty and not return null
  const allowEmpty =
    (viewStyle === 'list' && field.settings?.stretchX) ||
    (viewStyle === 'gallery' && field.settings?.stretchY) ||
    (viewStyle === 'carousel' && field.settings?.stretchY)

  switch (field.id) {
    case 'listAvatar':
    case 'magazineAvatar':
      return (
        <ListAvatar
          field={field}
          mediaUrl={mediaUrl}
          post={post}
          highlighted={highlighted}
        />
      )
    case 'galleryBanner':
      return (
        <GalleryBanner
          field={field}
          mediaUrl={mediaUrl}
          aspectRatio={field.settings?.aspectRatio}
          post={post}
          highlighted={highlighted}
          preview={preview}
        />
      )
    case 'cardBanner':
      return (
        <CardBanner
          field={field}
          mediaUrl={mediaUrl}
          aspectRatio={field.settings?.aspectRatio}
          post={post}
          highlighted={highlighted}
          linkTo={linkTo}
          preview={preview}
          isSinglePost={isSinglePost}
        />
      )
    case 'listPostSummary':
      return (
        <ListPostSummary
          field={field}
          highlighted={highlighted}
          stretchX={stretchX}
          fieldProps={{
            post,
            activeFieldId,
            postTypes,
            viewStyle,
            linkTo,
            preview,
          }}
          FieldRenderer={Field}
        />
      )
    case 'galleryMemberInfo':
    case 'cardMemberInfo':
      return (
        <GalleryMemberInfo
          post={post}
          highlighted={highlighted}
          stretchX={stretchX}
          showSpaceName={field.settings?.showSpaceName}
          hasPostOptions={viewStyle === 'card'}
          showPostOptions={field.settings?.showPostOptions}
        />
      )
    case 'listPostTitle':
    case 'galleryPostTitle':
      return (
        <PostTitle
          field={field}
          post={post}
          highlighted={highlighted}
          stretchX={stretchX}
          titleSizeClass={titleSizeClass}
          linkTo={linkTo}
          preview={preview}
          isSinglePost={isSinglePost}
        />
      )
    case 'listPostContent':
      return (
        <GalleryPostContent
          field={field}
          post={post}
          highlighted={highlighted}
          stretchX={stretchX}
          lineClamp="sm"
          preview={preview}
        />
      )
    case 'galleryPostContent':
      return (
        <GalleryPostContent
          field={field}
          post={post}
          highlighted={highlighted}
          stretchX={stretchX}
          lineClamp="lg"
          preview={preview}
        />
      )
    case 'cardPostContent':
      return (
        <CardPostContent
          field={field}
          post={post}
          highlighted={highlighted}
          stretchX={stretchX}
          displayStyle={field.settings?.displayStyle}
          truncateSize={field.settings?.truncateSize}
          preview={preview}
        />
      )
    case 'listTags':
    case 'tags':
      return (
        <Tags
          post={post}
          highlighted={highlighted}
          stretchX={stretchX}
          preview={preview}
        />
      )
    case 'reactionCount':
      return (
        <ReactionCount
          post={post}
          highlighted={highlighted}
          stretchX={stretchX}
        />
      )
    case 'cardPostInfoBar':
      return (
        <InfoBarField
          field={field}
          post={post}
          highlighted={highlighted}
          stretchX={stretchX}
          preview={preview}
        />
      )
    case 'replyCount':
      return (
        <ReplyCount post={post} highlighted={highlighted} stretchX={stretchX} />
      )
    case 'publishDate':
      return (
        <PublishDate
          field={field}
          post={post}
          highlighted={highlighted}
          stretchX={stretchX}
        />
      )
    case 'actionBar':
      return (
        <ActionBar
          post={post}
          highlighted={highlighted}
          stretchX={stretchX}
          buttonVariant={field.settings?.buttonVariant}
          fullWidth={field.settings?.fullWidth}
        />
      )
    case 'reactButton':
      return (
        <ReactButton
          post={post}
          highlighted={highlighted}
          stretchX={stretchX}
          buttonVariant={field.settings?.buttonVariant}
        />
      )
    case 'replyList':
      return (
        <ReplyListField
          post={post}
          highlighted={highlighted}
          stretchX={stretchX}
          postTypes={postTypes}
          isSinglePost={isSinglePost}
        />
      )
    case 'postInfoBar':
      return (
        <PostInfoBarField
          post={post}
          highlighted={highlighted}
          isSinglePost={isSinglePost}
          showBackToSpaceButton={field.settings?.showBackToSpaceButton}
        />
      )
    case 'pinnedReply':
      return (
        <PinnedReply
          field={field}
          post={post}
          highlighted={highlighted}
          preview={preview}
        />
      )
    default:
      return (
        <>
          {postTypes
            ?.find(postType => postType?.id === post?.postTypeId)
            ?.postFields?.fields.filter(postField => postField.key === field.id)
            .map(postField => (
              <FieldView
                highlighted={highlighted}
                key={postField?.key}
                field={{
                  ...postField,
                  settings: field.settings?.customFieldSettings,
                }}
                entity={post}
                className={clsx(
                  'mt-0 min-w-0 break-words',
                  !allowEmpty && 'empty:hidden',
                  postField.type !== CustomFieldType.text &&
                    'flex items-center',
                  stretchX,
                )}
                preview={preview}
                allowEmpty={allowEmpty}
              />
            ))}
        </>
      )
  }
}
